import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Gift, Copy, Check, Share2, Users, ChevronRight,
  Clock, CheckCircle, Star, ArrowRight, Loader2, Phone
} from 'lucide-react';
import IndividualBottomNav from '@/components/IndividualBottomNav';
import IndividualSidebar from '@/components/IndividualSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  registered:          { label: 'Registered',        color: 'bg-amber-100 text-amber-700',   icon: Clock },
  first_order_placed:  { label: 'First Order Placed', color: 'bg-blue-100 text-blue-700',    icon: CheckCircle },
  reward_applied:      { label: 'Reward Applied',     color: 'bg-emerald-100 text-emerald-700', icon: Star },
};

const IndividualReferrals = () => {
  const navigate = useNavigate();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [codeCopied, setCodeCopied] = useState(false);

  const token = localStorage.getItem('individualToken');

  useEffect(() => {
    if (!token) { navigate('/individual/login'); return; }
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/individual/referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (!data?.referral_code) return;
    navigator.clipboard.writeText(data.referral_code);
    setCodeCopied(true);
    toast.success('Referral code copied!');
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const getShareText = () => {
    const code = data?.referral_code || '';
    return `Hey! I use JustFold for laundry pickup in Gurgaon 🧺\nGet 25% off your first order — sign up with my code:\n👉 ${code}\nRegister here: https://justfold.in/individual/login?ref=${code}`;
  };

  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(getShareText());
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'JustFold Referral', text: getShareText() });
      } catch { /* cancelled */ }
    } else {
      handleCopyCode();
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <IndividualSidebar />
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Mobile topbar */}
        <header className="md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 flex-shrink-0 z-10">
          <div className="w-8 h-8 bg-orange-50 rounded-xl flex items-center justify-center">
            <Gift className="w-4 h-4 text-orange-500" />
          </div>
          <h1 className="font-black text-slate-800 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Refer &amp; Earn</h1>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-4 md:px-8 py-6 pb-24 md:pb-8 space-y-5">

            {/* Page heading (desktop) */}
            <div className="hidden md:block">
              <h1 className="text-2xl font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Refer &amp; Earn</h1>
              <p className="text-slate-400 text-sm mt-0.5">Share your code — your friend gets 25% off, you get 25% discount</p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
              </div>
            ) : data && (
              <>
                {/* First-order discount banner — shown if this customer was referred and discount not yet used */}
                {data.first_order_discount_pending && (
                  <div className="bg-gradient-to-r from-violet-500 to-violet-600 rounded-[2rem] px-6 py-5 text-white shadow-lg shadow-violet-200 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                    <div className="relative z-10 flex items-center gap-4">
                      <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0 text-2xl">🎁</div>
                      <div>
                        <p className="font-black text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>25% off your first order!</p>
                        <p className="text-violet-100 text-sm mt-0.5">
                          Referred by <span className="font-bold text-white">{data.referrer_name || data.referred_by_code}</span>. Discount applies automatically on your first order.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Hero explainer card */}
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-[2rem] px-6 py-7 text-white shadow-lg shadow-orange-200 relative overflow-hidden">
                  <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-2xl">🎉</div>
                      <div>
                        <p className="font-black text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>Invite friends, earn rewards</p>
                        <p className="text-orange-100 text-sm">Both of you benefit!</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-white/15 rounded-2xl px-4 py-3 text-center">
                        <p className="text-2xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>25%</p>
                        <p className="text-orange-100 text-xs mt-0.5">off for your friend</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-orange-200 flex-shrink-0" />
                      <div className="flex-1 bg-white/15 rounded-2xl px-4 py-3 text-center">
                        <p className="text-2xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>25%</p>
                        <p className="text-orange-100 text-xs mt-0.5">discount for you</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Your code */}
                <div className="bg-white border border-orange-100 rounded-[2rem] px-6 py-6 shadow-sm">
                  <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Your Referral Code</p>
                  <div className="bg-orange-50 rounded-2xl px-4 py-4 mb-4 text-center">
                    <p className="text-3xl font-black text-orange-600 tracking-[0.2em] font-mono break-all" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {data.referral_code || '—'}
                    </p>
                  </div>

                  {/* Share buttons */}
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={handleCopyCode}
                      className="flex flex-col items-center justify-center gap-1 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs transition-all">
                      {codeCopied
                        ? <><Check className="w-5 h-5 text-emerald-600" /><span className="text-emerald-600">Copied!</span></>
                        : <><Copy className="w-5 h-5" /><span>Copy</span></>}
                    </button>
                    <button onClick={handleWhatsAppShare}
                      className="flex flex-col items-center justify-center gap-1 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-xs transition-all">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      <span>WhatsApp</span>
                    </button>
                    <button onClick={handleNativeShare}
                      className="flex flex-col items-center justify-center gap-1 py-3 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-600 font-semibold text-xs transition-all">
                      <Share2 className="w-5 h-5" />
                      <span>Share</span>
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white border border-slate-200 rounded-[2rem] px-5 py-5 shadow-sm text-center">
                    <p className="text-3xl font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>{data.total_referrals}</p>
                    <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mt-1">Friends Referred</p>
                  </div>
                  {data.pending_reward_count > 0 ? (
                    <div className="bg-amber-50 border border-amber-100 rounded-[2rem] px-5 py-5 shadow-sm text-center">
                      <p className="text-3xl font-black text-amber-600" style={{ fontFamily: 'Manrope, sans-serif' }}>{data.pending_reward_count}</p>
                      <p className="text-xs text-amber-500 font-semibold uppercase tracking-wide mt-1">{data.pending_reward_count === 1 ? 'Reward' : 'Rewards'} Pending</p>
                      <p className="text-[10px] text-amber-400 mt-0.5">25% discount each, one per order</p>
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-[2rem] px-5 py-5 shadow-sm text-center">
                      <p className="text-3xl font-black text-slate-300" style={{ fontFamily: 'Manrope, sans-serif' }}>—</p>
                      <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mt-1">No Reward Yet</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">Refer a friend to earn 25%</p>
                    </div>
                  )}
                </div>

                {/* Referred friends list */}
                <div className="bg-white border border-slate-200 rounded-[2rem] px-6 py-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Friends You Referred</h3>
                      <p className="text-xs text-slate-400">{data.referrals?.length || 0} friend{(data.referrals?.length || 0) !== 1 ? 's' : ''} joined using your code</p>
                    </div>
                  </div>

                  {!data.referrals?.length ? (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-3">🤝</div>
                      <p className="font-semibold text-slate-500 text-sm">No referrals yet</p>
                      <p className="text-xs text-slate-400 mt-1">Share your code above to get started</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {data.referrals.map((r, i) => {
                        const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.registered;
                        const Icon = cfg.icon;
                        return (
                          <div key={i} className="p-3 bg-slate-50 rounded-2xl space-y-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center font-black text-orange-600 text-sm flex-shrink-0">
                                {(r.referee_name || '?')[0].toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-slate-800 text-sm truncate">{r.referee_name || '—'}</p>
                                {r.referee_phone && (
                                  <p className="text-[11px] text-slate-400 flex items-center gap-1">
                                    <Phone className="w-2.5 h-2.5 flex-shrink-0" />{r.referee_phone}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${cfg.color}`}>
                                <Icon className="w-2.5 h-2.5" />
                                {cfg.label}
                              </span>
                              {r.registered_at && (
                                <p className="text-[10px] text-slate-400">
                                  {new Date(r.registered_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* How it works */}
                <div className="bg-white border border-slate-200 rounded-[2rem] px-6 py-6 shadow-sm">
                  <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">How It Works</p>
                  <div className="space-y-3">
                    {[
                      { step: '1', icon: '📤', title: 'Share your code', desc: 'Copy your code or share via WhatsApp' },
                      { step: '2', icon: '✅', title: 'Friend registers', desc: 'They sign up with your code at JustFold' },
                      { step: '3', icon: '🎁', title: 'Friend gets 25% off', desc: "Auto-applied on their very first order — no code needed" },
                      { step: '4', icon: '💰', title: 'You earn 25% discount', desc: 'Auto-applied on your next order when your friend places their first order' },
                    ].map(({ step, icon, title, desc }) => (
                      <div key={step} className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-orange-50 rounded-xl flex items-center justify-center text-base flex-shrink-0">{icon}</div>
                        <div>
                          <p className="font-semibold text-slate-800 text-sm">{title}</p>
                          <p className="text-xs text-slate-400">{desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Referred-by strip */}
                {data.is_referred && (
                  <div className="bg-violet-50 border border-violet-100 rounded-[2rem] px-5 py-4 flex items-center gap-3">
                    <div className="w-9 h-9 bg-violet-100 rounded-xl flex items-center justify-center text-violet-600 flex-shrink-0">
                      <Gift className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-black text-slate-500 uppercase tracking-widest">Referred by</p>
                      <p className="font-bold text-violet-700 text-sm">{data.referrer_name || data.referred_by_code}</p>
                    </div>
                    {data.first_order_discount_pending
                      ? <span className="text-[10px] font-black text-violet-600 bg-white border border-violet-200 px-2 py-1 rounded-full whitespace-nowrap">10% discount waiting</span>
                      : <span className="text-[10px] font-black text-emerald-600 bg-white border border-emerald-200 px-2 py-1 rounded-full whitespace-nowrap">Discount used ✓</span>
                    }
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      <IndividualBottomNav />
    </div>
  );
};

export default IndividualReferrals;
